/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, HelpCircle, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Empty, ErrorText, Field, Input, Modal, PageHeader, Select, Spinner, Tabs, Textarea } from '@/components/ui';
import { api } from '@/lib/api';
import { fmtDate } from '@/lib/format';

type Tab = 'stats' | 'reviews' | 'digest';

/** M21 Аналитика судебной практики: статистика исходов собственных дел, обзоры практики по критериям, дайджест изменений */
export function PracticePage() {
  const [tab, setTab] = useState<Tab>('stats');
  return (
    <>
      <PageHeader title="Судебная практика" subtitle="Статистика по своим делам, обзоры актов по критериям, дайджест изменений" />
      <Tabs tabs={[{ id: 'stats', label: 'Статистика исходов' }, { id: 'reviews', label: 'Обзоры практики' }, { id: 'digest', label: 'Дайджест' }]} value={tab} onChange={setTab} />
      <div className="mt-4">
        {tab === 'stats' && <Stats />}
        {tab === 'reviews' && <Reviews />}
        {tab === 'digest' && <Digest />}
      </div>
    </>
  );
}

function Stats() {
  const [by, setBy] = useState('judge');
  const { data, isLoading } = useQuery({ queryKey: ['practice-stats', by], queryFn: () => api.get<any[]>(`/practice/stats?by=${by}`) });
  return (
    <div className="card p-4">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Select className="w-56" value={by} onChange={(e) => setBy(e.target.value)} options={[{ value: 'judge', label: 'По судьям' }, { value: 'court', label: 'По судам' }, { value: 'category', label: 'По категориям споров' }]} />
        <span className="text-sm text-slate-500">Исходы берутся из карточек судебных дел (поле «Итог»). Учитываются только дела вашей организации.</span>
      </div>
      {isLoading ? <Spinner /> : !data?.length ? <Empty>Нет судебных дел</Empty> : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead><tr><th className="th">{by === 'judge' ? 'Судья' : by === 'court' ? 'Суд' : 'Категория'}</th><th className="th text-right">Всего</th><th className="th text-right">Выиграно</th><th className="th text-right">Частично</th><th className="th text-right">Проиграно</th><th className="th text-right">В работе</th><th className="th">Успешность</th></tr></thead>
          <tbody>{data.map((r) => (
            <tr key={r.key} className="border-t border-slate-100">
              <td className="td">{r.key}</td><td className="td text-right tabular-nums">{r.total}</td><td className="td text-right tabular-nums text-green-700">{r.won}</td><td className="td text-right tabular-nums">{r.partial}</td><td className="td text-right tabular-nums text-red-700">{r.lost}</td><td className="td text-right tabular-nums text-slate-500">{r.open}</td>
              <td className="td">{r.winRate != null ? <div className="flex items-center gap-2"><div className="h-2 w-24 rounded bg-slate-100"><div className="h-2 rounded bg-brand-600" style={{ width: `${r.winRate}%` }} /></div><span className="tabular-nums">{r.winRate}%</span></div> : '-'}<div className="text-xs text-slate-400">{r.note}</div></td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
    </div>
  );
}

function Reviews() {
  const qc = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [f, setF] = useState({ question: '', query: '', criteria: '' });
  const { data } = useQuery({ queryKey: ['practice-reviews'], queryFn: () => api.get<any[]>('/practice/reviews') });
  const make = useMutation({
    mutationFn: () => api.post<any>('/practice/reviews', { question: f.question, query: f.query || f.question, criteria: f.criteria.split('\n').map((s) => s.trim()).filter((s) => s.length >= 3).slice(0, 10) }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['practice-reviews'] }); setCreating(false); setOpen(r.id); },
  });
  return (
    <div className="card p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="text-sm text-slate-500">Акты берутся из ваших документов с распознанным текстом. Цитаты сверяются с текстом акта.</div>
        <Button onClick={() => setCreating(true)}>Новый обзор</Button>
      </div>
      <ul className="divide-y divide-slate-100">
        {(data ?? []).map((r) => (
          <li key={r.id} className="flex cursor-pointer items-center justify-between gap-3 py-2 hover:bg-slate-50" onClick={() => setOpen(r.id)}>
            <span className="text-sm">{r.question}</span><span className="shrink-0 text-xs text-slate-500">{r.acts} актов · {fmtDate(r.createdAt)}</span>
          </li>
        ))}
        {!data?.length && <li className="py-2 text-sm text-slate-500">Обзоров пока нет</li>}
      </ul>
      <Modal title="Новый обзор практики" open={creating} onClose={() => setCreating(false)} wide>
        <div className="space-y-3">
          <Field label="Вопрос"><Textarea value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} placeholder="Например: признают ли суды сделку недействительной при неравноценном встречном исполнении в пределах года до банкротства" /></Field>
          <Field label="Поиск актов" hint="По умолчанию ищется по тексту вопроса, до 20 наиболее близких документов"><Input value={f.query} onChange={(e) => setF({ ...f, query: e.target.value })} /></Field>
          <Field label="Критерии (по одному в строке)" hint="Если оставить пустым, критерии сформулирует ИИ"><Textarea rows={4} value={f.criteria} onChange={(e) => setF({ ...f, criteria: e.target.value })} /></Field>
          <ErrorText error={make.error} />
          {make.isPending && <div className="text-sm text-slate-500">Анализирую акты, это может занять несколько минут...</div>}
          <Button disabled={f.question.trim().length < 5 || make.isPending} onClick={() => make.mutate()}>Составить обзор</Button>
        </div>
      </Modal>
      <Modal title="Обзор практики" open={!!open} onClose={() => setOpen(null)} wide>{open && <ReviewView id={open} />}</Modal>
    </div>
  );
}

function Mark({ met, verified }: { met: boolean | null; verified: boolean }) {
  const icon = met === true ? <Check size={16} className="text-green-700" /> : met === false ? <X size={16} className="text-red-700" /> : <HelpCircle size={16} className="text-slate-400" />;
  return <span className="inline-flex items-center gap-1">{icon}{met != null && !verified && <span title="Цитата не найдена в тексте акта" className="text-xs text-amber-600">?</span>}</span>;
}

export function ReviewView({ id }: { id: string }) {
  const { data: r } = useQuery({ queryKey: ['practice-review', id], queryFn: () => api.get<any>(`/practice/reviews/${id}`) });
  const [quote, setQuote] = useState<string | null>(null);
  if (!r) return <Spinner />;
  return (
    <div className="space-y-4">
      <div className="text-sm font-medium">{r.question}</div>
      <table className="w-full text-sm">
        <thead><tr><th className="th">Критерий</th><th className="th text-right">Да</th><th className="th text-right">Нет</th><th className="th text-right">Нет данных</th></tr></thead>
        <tbody>{r.criteria.map((c: any) => <tr key={c.criterion} className="border-t border-slate-100"><td className="td">{c.criterion}</td><td className="td text-right text-green-700">{c.yes}</td><td className="td text-right text-red-700">{c.no}</td><td className="td text-right text-slate-500">{c.unknown}</td></tr>)}</tbody>
      </table>
      {r.summary && <div className="whitespace-pre-wrap rounded bg-slate-50 p-3 text-sm">{r.summary}</div>}
      <div className="overflow-x-auto"><table className="w-full text-sm">
        <thead><tr><th className="th">#</th><th className="th">Акт</th>{r.criteria.map((_: any, i: number) => <th key={i} className="th text-center">К{i + 1}</th>)}</tr></thead>
        <tbody>{r.acts.map((a: any, n: number) => (
          <tr key={a.documentId} className="border-t border-slate-100">
            <td className="td">[{n + 1}]</td>
            <td className="td"><Link className="text-brand-700 hover:underline" to={`/documents?open=${a.documentId}`}>{a.number ?? a.title}</Link><div className="text-xs text-slate-500">{[a.court, a.date].filter(Boolean).join(', ')}</div>{a.outcome && <div className="text-xs">{a.outcome}</div>}{a.error && <Badge tone="red">ошибка ИИ</Badge>}</td>
            {a.criteria.map((c: any, i: number) => <td key={i} className="td cursor-pointer text-center" onClick={() => c.quote && setQuote(c.quote)}><Mark met={c.met} verified={c.verified} /></td>)}
          </tr>
        ))}</tbody>
      </table></div>
      {quote && <div className="rounded border-l-4 border-brand-600 bg-slate-50 p-3 text-sm italic">«{quote}»</div>}
      <div className="text-xs text-slate-400">Нажмите на отметку, чтобы увидеть цитату. Знак «?» означает, что цитата не найдена дословно и требует проверки.</div>
    </div>
  );
}

function Digest() {
  const [topics, setTopics] = useState('');
  const run = useMutation({ mutationFn: () => api.post<any>('/practice/digest', { topics: topics || undefined }) });
  const d = run.data;
  return (
    <div className="card space-y-3 p-4">
      <div className="flex flex-wrap gap-2">
        <Input className="flex-1" placeholder="Темы через запятую (по умолчанию: банкротство, взыскание, исполнительное производство)" value={topics} onChange={(e) => setTopics(e.target.value)} />
        <Button disabled={run.isPending} onClick={() => run.mutate()}>Собрать дайджест</Button>
      </div>
      <ErrorText error={run.error} />
      {run.isPending && <Spinner />}
      {d?.note && <div className="text-sm text-slate-600">{d.note}</div>}
      {d?.digest && <div className="whitespace-pre-wrap rounded bg-slate-50 p-3 text-sm">{d.digest}</div>}
      {!!d?.items?.length && <ul className="space-y-1 text-sm">{d.items.map((i: any, n: number) => <li key={n}><a className="text-brand-700 hover:underline" href={i.link} target="_blank" rel="noreferrer">{i.title}</a> <span className="text-xs text-slate-500">{i.source}</span></li>)}</ul>}
    </div>
  );
}
